import { forwardRef, useState, useId } from "react";
import { Eye, EyeSlash, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuroraLabels } from "../auroraLabels";
import styles from "./Input.module.css";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  hint?: string;
  error?: string;
  icon?: React.ReactNode;
  variant?: "text" | "password" | "search" | "textarea";
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      icon,
      variant = "text",
      className,
      id: externalId,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = externalId ?? generatedId;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const [showPassword, setShowPassword] = useState(false);
    // LOKALE afwijking (issue 294): de naam van de oog-knop komt uit de
    // AuroraLabels-context in plaats van een vaste Engelse tekst.
    const labels = useAuroraLabels();

    const hasLeftIcon = !!icon || variant === "search";
    const hasRightIcon = variant === "password" || variant === "search";

    const inputClasses = [
      styles.input,
      variant === "textarea" && styles.textarea,
      hasLeftIcon && styles.hasLeftIcon,
      hasRightIcon && styles.hasRightIcon,
      error && styles.inputError,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const describedBy = [hint && hintId, error && errorId]
      .filter(Boolean)
      .join(" ") || undefined;

    const sharedProps = {
      id,
      className: inputClasses,
      disabled,
      "aria-invalid": error ? true : undefined,
      "aria-describedby": describedBy,
      ...rest,
    };

    const renderInput = () => {
      if (variant === "textarea") {
        return (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            {...(sharedProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          />
        );
      }

      const type =
        variant === "password"
          ? showPassword
            ? "text"
            : "password"
          : variant === "search"
            ? "search"
            : "text";

      return (
        <input
          ref={ref as React.Ref<HTMLInputElement>}
          type={type}
          {...sharedProps}
        />
      );
    };

    return (
      <div className={styles.wrapper}>
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>

        <div className={styles.inputWrapper}>
          {hasLeftIcon && (
            <span className={styles.iconLeft}>
              {variant === "search" ? (
                <MagnifyingGlass size={18} weight="regular" />
              ) : (
                icon
              )}
            </span>
          )}

          {renderInput()}

          {variant === "password" && (
            <span className={styles.iconRight}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
                aria-label={showPassword ? labels.passwordHide : labels.passwordShow}
              >
                {showPassword ? (
                  <EyeSlash size={18} weight="regular" />
                ) : (
                  <Eye size={18} weight="regular" />
                )}
              </button>
            </span>
          )}

          {variant === "search" && (
            <span className={styles.iconRight}>
              <span className={styles.badge}>⌘K</span>
            </span>
          )}
        </div>

        {hint && !error && (
          <span id={hintId} className={styles.hint}>
            {hint}
          </span>
        )}

        {error && (
          <span id={errorId} className={styles.errorText} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
export type { InputProps };
